import { allImages } from '@root/assets';
import {
  CELLS,
  cellForPosition,
  ENTRY_ARROW,
  FINISH_MULTIPLIER,
  LAYERS,
  LEFT_LANE_X,
  OPACITY,
  OVERLAY_IN_TILE,
  QUADRANT,
  ROW_PITCH,
  TILE_SIZE,
  TRANSITION,
  TURN_ARROW,
  boardScale,
  ghostPlacement,
} from '@root/lib/boardPath';
import {
  colouredAsset,
  readPawnColour,
  type PawnColour,
} from '@root/lib/pawnColour';
import type { LudoRollOutcome, LudoTile } from '@root/store/types/round';
import { phaserBridge, type ReactToPhaserEvent } from './bridge';
import { MULTIPLIER_ART, colourTextures, diceFaceKey } from './textureKeys';

// The design's two compositions. Everything derives from whichever fits.
const COMPOSITION = {
  landscape: {
    view: { width: 1440, height: 900 },
    board: { x: 251, y: 88, width: 320 },
    dice: { x: 885, y: 298, width: 180, height: 180 },
    // Where the whole four-sided board sits before the zoom.
    fullBoard: { x: 70, y: 125, width: 650 },
  },
  portrait: {
    view: { width: 390, height: 844 },
    board: { x: 86, y: 147, width: 207 },
    dice: { x: 22, y: 636, width: 102, height: 102 },
    fullBoard: { x: 24, y: 186, width: 342 },
  },
} as const;

// Shade over the background, per the design's Background shade layer. It
// sits under the lane, which keeps its own colour.
const BG_SHADE = { colour: 0x11071d, alpha: 0.35, depth: -0.5 };

const HOP_MS = 140;
const DICE_SHUFFLE_MS = 620;

export default class Level extends Phaser.Scene {
  // Must match what Preload fetched, or the first draw gets placeholders.
  private colour: PawnColour = readPawnColour();
  private board: LudoTile[] = [];
  private position = 0;

  private boardLayer!: Phaser.GameObjects.Container;
  private overlayLayer!: Phaser.GameObjects.Container;
  private pawn!: Phaser.GameObjects.Image;
  private die!: Phaser.GameObjects.Image;
  private diceFrame?: Phaser.GameObjects.Image;
  private bg?: Phaser.GameObjects.Image;
  private bgShade?: Phaser.GameObjects.Rectangle;
  private ghost?: Phaser.GameObjects.Image;
  private offBridge?: () => void;

  constructor() {
    super('Level');
  }

  create(): void {
    try {
      this.drawBackground();
      this.boardLayer = this.add.container(0, 0);
      this.overlayLayer = this.add.container(0, 0);
      this.drawBoard();
      this.drawDice();
      this.offBridge = phaserBridge.onReactCommand((e) => this.onCommand(e));
      this.scale.on(Phaser.Scale.Events.RESIZE, this.relayout, this);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.offBridge?.();
        this.scale.off(Phaser.Scale.Events.RESIZE, this.relayout, this);
      });
      phaserBridge.sendToReact({ type: 'SCENE_READY', scene: 'Level' });
    } catch (error) {
      phaserBridge.sendToReact({
        type: 'SCENE_ERROR',
        scene: 'Level',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  // Fitted, not cropped. The background does the covering.
  private get view(): { scale: number; x: number; y: number } {
    const { width, height } = this.scale;
    const { view } = this.composition;
    const scale = Math.min(width / view.width, height / view.height);
    return {
      scale,
      x: (width - view.width * scale) / 2,
      y: (height - view.height * scale) / 2,
    };
  }

  private get composition(): (typeof COMPOSITION)[keyof typeof COMPOSITION] {
    const { width, height } = this.scale;
    return height > width ? COMPOSITION.portrait : COMPOSITION.landscape;
  }

  // Lane, in canvas pixels.
  private get placement(): { x: number; y: number; width: number } {
    const { scale, x, y } = this.view;
    const { board } = this.composition;
    return {
      x: x + board.x * scale,
      y: y + board.y * scale,
      width: board.width * scale,
    };
  }

  private get scaleFactor(): number {
    return boardScale(this.placement.width);
  }

  // Lane units -> canvas pixels.
  private px(x: number, y: number): { x: number; y: number } {
    const s = this.scaleFactor;
    const { x: ox, y: oy } = this.placement;
    return { x: ox + x * s, y: oy + y * s };
  }

  private place(
    image: Phaser.GameObjects.Image,
    box: { x: number; y: number; width: number; height: number }
  ): Phaser.GameObjects.Image {
    const { x, y } = this.px(box.x, box.y);
    const s = this.scaleFactor;
    return image
      .setOrigin(0, 0)
      .setPosition(x, y)
      .setDisplaySize(box.width * s, box.height * s);
  }

  private drawBackground(): void {
    const { width, height } = this.scale;
    const portrait = height > width;
    this.bg?.destroy();
    this.bg = this.add
      .image(0, 0, portrait ? 'Background_Mobile' : 'Background')
      .setOrigin(0, 0)
      .setDisplaySize(width, height)
      .setDepth(-1);

    this.bgShade?.destroy();
    this.bgShade = this.add
      .rectangle(0, 0, width, height, BG_SHADE.colour, BG_SHADE.alpha)
      .setOrigin(0, 0)
      .setDepth(BG_SHADE.depth);
  }

  // Redraw at the new size. It's a handful of images.
  private relayout(): void {
    if (!this.boardLayer) return;
    this.drawBackground();
    this.drawBoard();
    this.drawOverlays();
    this.drawPawn();
    this.drawDice();
  }

  private drawBoard(): void {
    this.boardLayer.removeAll(true);
    const c = this.colour;

    // Gold pediment with the crown and the finish reward, above the grid.
    this.boardLayer.add(
      this.place(
        this.add.image(0, 0, colouredAsset('EndZoneTriangle', c)),
        LAYERS.triangle
      )
    );
    this.boardLayer.add(
      this.place(this.add.image(0, 0, colouredAsset('Crown', c)), LAYERS.crown)
    );
    // Reward sits under the crown.
    const reward = this.px(
      LAYERS.finishRewardText.x + LAYERS.finishRewardText.width / 2,
      LAYERS.finishRewardText.y + LAYERS.finishRewardText.height / 2
    );
    this.boardLayer.add(
      this.add
        .text(reward.x, reward.y, `${FINISH_MULTIPLIER}X`, {
          fontFamily: 'Lilita One, sans-serif',
          fontSize: `${Math.round(LAYERS.finishRewardText.height * this.scaleFactor)}px`,
          color: '#2e1040',
        })
        .setOrigin(0.5, 0.5)
    );
    // Names the square the pawn starts on.
    const start = this.px(
      LAYERS.startLabel.x,
      LAYERS.startLabel.y + LAYERS.startLabel.height / 2
    );
    this.boardLayer.add(
      this.add
        .text(start.x, start.y, 'START', {
          fontFamily: 'Nunito Sans, sans-serif',
          fontSize: `${Math.round(LAYERS.startLabel.height * this.scaleFactor)}px`,
          fontStyle: '800',
          color: '#fff2d7',
        })
        .setOrigin(0, 0.5)
    );
    this.boardLayer.add(
      this.place(this.add.image(0, 0, 'TileBox_Frame'), LAYERS.tileBox)
    );
    this.boardLayer.add(
      this.place(this.add.image(0, 0, 'FinishLine'), LAYERS.finishLine)
    );

    // Decorative lane; never walked on, and dimmed once the run is live.
    for (let row = 0; row < 6; row += 1) {
      const cell = {
        x: LEFT_LANE_X,
        y: CELLS[row].y,
        width: TILE_SIZE,
        height: TILE_SIZE,
      };
      this.boardLayer.add(
        this.place(this.add.image(0, 0, 'Tile_Ivory'), cell).setAlpha(
          OPACITY.zoomLeftLane
        )
      );
    }

    // The twelve cells the pawn walks.
    CELLS.forEach((cell) => {
      const key = cell.ivory ? 'Tile_Ivory' : colouredAsset('Tile', c);
      this.boardLayer.add(this.place(this.add.image(0, 0, key), cell));
    });

    this.drawArrow(
      ENTRY_ARROW.cell,
      ENTRY_ARROW.inTile,
      'PermanentRouteArrow_EntryDown'
    );
    this.drawArrow(
      TURN_ARROW.cell,
      TURN_ARROW.inTile,
      'PermanentRouteArrow_TurnUp'
    );

    this.drawPawn();
    this.drawOverlays();
  }

  private drawArrow(
    cell: number,
    inTile: { x: number; y: number; width: number; height: number },
    key: string
  ): void {
    const base = CELLS[cell - 1];
    this.boardLayer.add(
      this.place(this.add.image(0, 0, key), {
        x: base.x + inTile.x,
        y: base.y + inTile.y,
        width: inTile.width,
        height: inTile.height,
      })
    );
  }

  // Power-ups come from game state, never from the composed example board.
  private drawOverlays(): void {
    this.overlayLayer.removeAll(true);
    this.board.forEach((tile) => {
      if (tile.type === 'NORMAL') return;
      const cell = CELLS[cellForPosition(tile.position) - 1];
      if (!cell) return;

      // The free-roll marker on the turn tile is inset to clear the arrow.
      const inset =
        tile.type === 'FREE_ROLL' && cell.cell === TURN_ARROW.cell
          ? OVERLAY_IN_TILE.freeRollOnTurnTile
          : OVERLAY_IN_TILE.standard;
      const box = {
        x: cell.x + inset.x,
        y: cell.y + inset.y,
        width: inset.width,
        height: inset.height,
      };

      if (tile.type === 'SKULL') {
        this.overlayLayer.add(this.place(this.add.image(0, 0, 'Skull'), box));
        return;
      }
      if (tile.type === 'FREE_ROLL') {
        this.overlayLayer.add(
          this.place(this.add.image(0, 0, 'FreeRoll_Marker'), box)
        );
        return;
      }
      const art = MULTIPLIER_ART[tile.multiplier ?? 2];
      if (!art) return;
      this.overlayLayer.add(this.place(this.add.image(0, 0, art.star), box));
      this.overlayLayer.add(this.place(this.add.image(0, 0, art.text), box));
    });
  }

  private drawPawn(): void {
    this.pawn?.destroy();
    const cell = CELLS[cellForPosition(this.position) - 1] ?? CELLS[0];
    this.pawn = this.place(
      this.add.image(0, 0, colouredAsset('Pawn', this.colour)),
      cell
    );
    this.pawn.setDepth(10);
  }

  // Frame with the current face on top, same box.
  private drawDice(): void {
    const { scale, x: ox, y: oy } = this.view;
    const { dice } = this.composition;
    const x = ox + dice.x * scale;
    const y = oy + dice.y * scale;
    const w = dice.width * scale;
    const h = dice.height * scale;

    this.diceFrame?.destroy();
    this.diceFrame = this.add
      .image(x, y, colouredAsset('DiceFrame', this.colour))
      .setOrigin(0, 0)
      .setDisplaySize(w, h);

    const face = this.die?.texture.key ?? diceFaceKey(1);
    this.die?.destroy();
    this.die = this.add.image(x, y, face).setOrigin(0, 0).setDisplaySize(w, h);
  }

  // Preload fetched only the colour in play; pull another's art first.
  private withColour(colour: PawnColour, then: () => void): void {
    const missing = colourTextures(colour).filter(
      (key) => !this.textures.exists(key)
    );
    if (missing.length === 0) {
      then();
      return;
    }
    missing.forEach((key) => this.load.image(key, allImages[key]));
    this.load.once(Phaser.Loader.Events.COMPLETE, then);
    this.load.start();
  }

  private onCommand(event: ReactToPhaserEvent): void {
    switch (event.type) {
      case 'SET_COLOUR': {
        const next = event.colour;
        this.withColour(next, () => {
          this.colour = next;
          this.drawBoard();
        });
        break;
      }
      case 'SET_BOARD':
        this.board = event.tiles;
        this.drawOverlays();
        break;
      case 'SET_POSITION':
        this.position = event.position;
        this.drawPawn();
        break;
      case 'SHOW_LANE': {
        const lane = event.colour;
        this.withColour(lane, () => {
          this.colour = lane;
          this.ghost?.destroy();
          this.drawBoard();
        });
        break;
      }
      case 'PLAY_INTRO': {
        const intro = event.colour;
        this.withColour(intro, () => {
          this.colour = intro;
          this.playIntro();
        });
        break;
      }
      case 'PLAY_ROLL':
        this.playRoll(event.dice, event.from, event.to, event.outcome);
        break;
      default:
        break;
    }
  }

  // Hold the whole four-sided board, then zoom to the chosen lane.
  private playIntro(): void {
    this.drawBoard();
    const { scale, x: ox, y: oy } = this.view;
    const { fullBoard } = this.composition;

    this.ghost?.destroy();
    this.ghost = this.add
      .image(
        ox + (fullBoard.x + fullBoard.width / 2) * scale,
        oy + (fullBoard.y + fullBoard.width / 2) * scale,
        'FullBoard_Preparing_FourStartingAreas'
      )
      .setDisplaySize(fullBoard.width * scale, fullBoard.width * scale)
      .setDepth(20);

    // The lane arrives with the camera, so hold it back until the zoom runs.
    this.overlayLayer.setAlpha(0);
    this.boardLayer.setAlpha(0);

    this.time.delayedCall(TRANSITION.preparingHoldMs, () => {
      const lane = this.placement;
      const laneCentre = {
        x: lane.x + lane.width / 2,
        y: lane.y + (QUADRANT.height * this.scaleFactor) / 2,
      };
      const zoom = TRANSITION.zoomMs;

      // Push in on the player's arm, accelerating as it closes.
      this.tweens.add({
        targets: this.ghost,
        x: laneCentre.x,
        y: laneCentre.y,
        scaleX: (this.ghost?.scaleX ?? 1) * 2.4,
        scaleY: (this.ghost?.scaleY ?? 1) * 2.4,
        angle: -12,
        duration: zoom,
        ease: 'Quad.easeIn',
      });

      // Fade held back so the board stays readable through the push.
      this.tweens.add({
        targets: this.ghost,
        alpha: 0,
        duration: zoom * 0.45,
        delay: zoom * 0.55,
        ease: 'Sine.easeIn',
        onComplete: () => {
          this.ghost?.destroy();
          this.ghost = undefined;
          phaserBridge.sendToReact({ type: 'INTRO_COMPLETE' });
        },
      });

      // Lane rises underneath so the camera arrives on something solid.
      this.tweens.add({
        targets: this.boardLayer,
        alpha: 1,
        duration: zoom * 0.55,
        delay: zoom * 0.4,
        ease: 'Sine.easeOut',
      });

      // Symbols land last, as the spec asks.
      this.tweens.add({
        targets: this.overlayLayer,
        alpha: 1,
        duration: zoom * 0.35,
        delay: zoom * 0.6,
        ease: 'Sine.easeOut',
      });
    });
  }

  private playRoll(
    dice: number,
    from: number,
    to: number,
    outcome: LudoRollOutcome
  ): void {
    this.shuffleDice(dice, () => {
      // An overshoot wastes the roll: the pawn stays exactly where it was.
      if (to === from) {
        this.nudgePawn();
        this.finishRoll(from, outcome);
        return;
      }
      this.hopTo(from, to, () => this.finishRoll(to, outcome));
    });
  }

  private shuffleDice(result: number, done: () => void): void {
    let elapsed = 0;
    const tick = this.time.addEvent({
      delay: 70,
      loop: true,
      callback: () => {
        elapsed += 70;
        if (elapsed >= DICE_SHUFFLE_MS) {
          tick.remove();
          this.die.setTexture(diceFaceKey(result));
          done();
          return;
        }
        this.die.setTexture(diceFaceKey(Phaser.Math.Between(1, 6)));
      },
    });
  }

  // Step cell by cell so the turn at the bottom reads as a path, not a jump.
  private hopTo(from: number, to: number, done: () => void): void {
    const steps: number[] = [];
    for (let p = from + 1; p <= Math.min(to, CELLS.length - 1 + 1); p += 1) {
      steps.push(p);
    }
    const walk = (i: number): void => {
      if (i >= steps.length) {
        this.position = to;
        done();
        return;
      }
      const cell = CELLS[cellForPosition(steps[i]) - 1];
      const target = cell
        ? this.px(cell.x, cell.y)
        : this.px(
            CELLS[CELLS.length - 1].x,
            CELLS[CELLS.length - 1].y - ROW_PITCH
          );
      this.tweens.add({
        targets: this.pawn,
        x: target.x,
        y: target.y,
        duration: HOP_MS,
        ease: 'Quad.easeInOut',
        onComplete: () => walk(i + 1),
      });
    };
    walk(0);
  }

  private nudgePawn(): void {
    this.tweens.add({
      targets: this.pawn,
      y: this.pawn.y - 6 * this.scaleFactor,
      duration: 110,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  private finishRoll(position: number, outcome: LudoRollOutcome): void {
    this.position = position;
    phaserBridge.sendToReact({ type: 'ROLL_COMPLETE', position, outcome });
  }
}

// Board width in design units, exported for layout maths elsewhere.
export const BOARD_DESIGN_WIDTH = QUADRANT.width;
